import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Breadcrumbs,
  Button,
  Download,
  TiltedTopArrow,
  Tooltip,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import {
  TextWithIcon,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import contractAIQConstants from '../../../core-utils/messages/ContractAIQConstants';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';

export interface ContractReportTemplateProps {
  breadcrumbsList: any[];
  reportName: string;
  document: string;
  handleDocumentOnclick: () => void;
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  handleDownloadReport: () => void;
  handleReportSummary: () => void;
  isReportSummaryActive?: boolean;
  reportTooltip?: string;
  showDownloadReport?: boolean;
}

const ContractReportTemplate: React.FC<ContractReportTemplateProps> = ({
  breadcrumbsList,
  reportName,
  leftContent,
  rightContent,
  document,
  handleDownloadReport,
  handleReportSummary,
  isReportSummaryActive,
  handleDocumentOnclick,
  reportTooltip,
  showDownloadReport,
}) => {
  const useStyles = makeStyles()({
    root: {
      background: patientAccessTheme.palette.status.white,
    },
    header: {
      padding: '12px 20px',
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    },
    body: {
      display: 'flex',
      height: '85vh',
    },
    leftPart: {
      minWidth: '402px',
      overflow: 'auto',
      overflowX: 'hidden',
      ...scrollBarStyles,
    },
    rightPart: {
      width: '100%',
      padding: isReportSummaryActive ? '36px' : '60px 104px',
      borderLeft: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      background: isReportSummaryActive
        ? patientAccessTheme.palette.status.white
        : patientAccessTheme.palette.grey[100],
      overflow: 'auto',
      ...scrollBarStyles,
    },
    reportSummary: {
      display: 'flex',
      height: '104px',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      cursor: 'pointer',
      background: isReportSummaryActive
        ? patientAccessTheme.palette.grey[100]
        : patientAccessTheme.palette.status.white,
    },
    tiltedArrowIcon: {
      width: '14px',
      height: '14px',
    },
  });

  //constants
  const { classes } = useStyles();
  const constants = contractAIQConstants.CONTRACT_REPORTS;

  return (
    <Grid className={classes.root}>
      <Grid className={classes.header}>
        <Breadcrumbs itemList={breadcrumbsList} />
      </Grid>
      <Grid className={classes.body}>
        <Grid className={classes.leftPart}>
          <Grid className={classes.reportSummary} onClick={handleReportSummary}>
            <TextWithIcon
              endIcon={
                reportTooltip && (
                  <Tooltip title={reportTooltip}>
                    <Info />
                  </Tooltip>
                )
              }
              label={
                <Typography
                  fontSize="16px"
                  fontType="semiBold"
                  lineHeight="140%"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {constants.REPORT_SUMMARY}
                </Typography>
              }
            />
            {showDownloadReport && (
              <Button
                label={
                  <Typography
                    fontSize="14px"
                    fontType="semiBold"
                    lineHeight="24px"
                    textColor={patientAccessTheme.palette.primary[500]}
                  >
                    {constants.DOWNLOAD_BUTTON}
                  </Typography>
                }
                startIcon={<Download strokeWidth={1} />}
                variant={'outlined'}
                color="primary"
                height="44px"
                width={'182px'}
                borderRadius="4px"
                onClick={handleDownloadReport}
              />
            )}
          </Grid>
          {leftContent && <Grid>{leftContent}</Grid>}
        </Grid>
        <Grid className={classes.rightPart}>
          {isReportSummaryActive && (
            <Grid>
              <Grid>
                <Typography
                  fontSize="16px"
                  fontType="semiBold"
                  lineHeight="140%"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                  margin="0px 0px 8px 0px"
                >
                  {reportName}
                </Typography>
                <TextWithIcon
                  label={
                    <Typography
                      fontSize="16px"
                      fontType="medium"
                      lineHeight="140%"
                      textColor={patientAccessTheme.palette.text.mediumemphasis}
                    >
                      {document}
                    </Typography>
                  }
                  endIcon={
                    false && (
                      <TiltedTopArrow className={classes.tiltedArrowIcon} />
                    )
                  }
                  endIconLeftMargin="8px"
                  handleOnClick={handleDocumentOnclick}
                  isClickable={false}
                />
              </Grid>
            </Grid>
          )}
          {rightContent && <Grid>{rightContent}</Grid>}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ContractReportTemplate;
